import React, { useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { Box, IconButton, Menu, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

export interface CalendarColorOption {
  key?: string;
  color: string;
}

export interface CalendarColorPickerProps {
  color: string;
  options: readonly CalendarColorOption[];
  onChange: (color: string) => void;
  disabled?: boolean;
  size?: number;
  /** i18n key prefix for option titles, e.g. room.colorNames */
  colorNamePrefix?: string;
  ariaLabel?: string;
}

/**
 * Tiny swatch that opens a compact color menu. Click does not bubble (safe inside toggle rows).
 */
const CalendarColorPicker: React.FC<CalendarColorPickerProps> = ({
  color,
  options,
  onChange,
  disabled = false,
  size = 14,
  colorNamePrefix = 'room.colorNames',
  ariaLabel,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title={t('spaceCalendar.changeColor', 'Change color')}>
        <span>
          <IconButton
            size="small"
            disabled={disabled}
            aria-label={ariaLabel ?? t('spaceCalendar.changeColor', 'Change color')}
            aria-haspopup="menu"
            aria-expanded={open ? 'true' : undefined}
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              setAnchorEl(event.currentTarget);
            }}
            sx={{
              p: 0.35,
              width: size + 10,
              height: size + 10,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: size,
                height: size,
                borderRadius: '50%',
                bgcolor: color,
                boxShadow: `0 0 0 2px ${alpha(color, 0.35)}`,
                border: '1px solid',
                borderColor: 'background.paper',
              }}
            />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              p: 1,
              mt: 0.5,
              minWidth: 0,
              borderRadius: 2,
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: 0.75,
            px: 0.25,
          }}
          role="listbox"
          aria-label={t('spaceCalendar.colorPalette', 'Color palette')}
        >
          {options.map((option) => {
            const selected = option.color.toLowerCase() === color.toLowerCase();
            const title = option.key
              ? t(`${colorNamePrefix}.${option.key}`, option.key)
              : option.color;
            return (
              <Box
                key={option.color}
                component="button"
                type="button"
                role="option"
                aria-selected={selected}
                title={title}
                onClick={(event) => {
                  event.stopPropagation();
                  setAnchorEl(null);
                  if (!selected) {
                    onChange(option.color);
                  }
                }}
                sx={{
                  width: 22,
                  height: 22,
                  p: 0,
                  borderRadius: '50%',
                  bgcolor: option.color,
                  border: '2px solid',
                  borderColor: selected ? 'common.white' : 'transparent',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: selected
                    ? `0 0 0 1px ${option.color}, 0 0 0 3px ${alpha(option.color, 0.45)}`
                    : `inset 0 0 0 1px ${alpha('#000', 0.25)}`,
                  '&:hover': {
                    transform: 'scale(1.12)',
                  },
                }}
              >
                {selected ? (
                  <CheckIcon
                    sx={{
                      fontSize: 14,
                      color: 'common.white',
                      filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.85))',
                    }}
                  />
                ) : null}
              </Box>
            );
          })}
        </Box>
      </Menu>
    </>
  );
};

export default CalendarColorPicker;
